/* eslint-disable no-template-curly-in-string */
import { isPresent } from '@jjvgaming/player-payback-library'
import { bool, date, number, object, string } from 'yup'
import {
  Alert,
  Button,
  FormControlLabel,
  Grid,
  InputLabel,
  Radio,
  RadioGroup,
} from '@mui/material'
import { TextField } from 'components/Shared/TextField'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { FormFieldErrorMessage } from 'components/FormFieldErrorMessage'
import { ActivityButton } from 'components/ActivityButton'
import { Modal } from 'components/Modal/Modal'
import { useLayout } from 'hooks/useLayout'
import { ModalFullHeader } from 'components/Modal/ModalFullHeader'
import { ModalFullBody } from 'components/Modal/ModalFullBody'
import { ModalFullFooter } from 'components/Modal/ModalFullFooter'
import { type RequiredId } from 'utils/typeUtils'
import { AdornedDateTimeField } from 'components/AdornedDateTimeField/AdornedDateTimeField'
import { isNil } from 'lodash'
import { formatISO, parseISO } from 'date-fns'
import { type SystemBannerDTO } from 'types/api'

interface EditBannerDetailsProps {
  banner: RequiredId<SystemBannerDTO>
  isModalOpen: boolean
  toggleModalIsOpen: () => void
  updateIsActive: boolean
  onSubmitBanner: (banner: RequiredId<SystemBannerDTO>) => void
}

export const EditBannerDetailsModal = ({
  banner,
  updateIsActive,
  isModalOpen,
  toggleModalIsOpen,
  onSubmitBanner,
}: EditBannerDetailsProps) => {
  const { isMobile } = useLayout()
  const heightModal = isMobile ? '100%' : '75%'
  const systemBannerTypes = ['Urgent', 'Informational']

  const getBannerDates = (banner: SystemBannerDTO) => {
    const startDate = isNil(banner.startDate)
      ? undefined
      : parseISO(banner.startDate)
    const endDate = isNil(banner.endDate) ? undefined : parseISO(banner.endDate)

    return { startDate, endDate }
  }

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const BannerDetailsSchema = object().shape(
    {
      Banner: object().shape({
        id: number(),
        title: string()
          .required('Title is a required field')
          .max(50, 'Title cannot exceed 50 characters'),
        content: string()
          .required('Banner Content is a required field')
          .max(150, 'Banner Content cannot exceed 150 characters'),
        type: string().required('Banner Type is required'),
        dismissable: bool().required('Dismissable option is required'),
        startDate: date()
          .typeError('Must enter a valid start date')
          .when('$isModalOpen', {
            is: true,
            then: (schema) =>
              schema
                .required('Must enter a start date')
                .min(today, 'Start date should not be a date in the past'),
            otherwise: (schema) => schema.notRequired(),
          }),
        endDate: date()
          .typeError('Must enter a valid end date')
          .when('startDate', (startDate, schema) => {
            if (startDate) {
              return schema.min(
                startDate,
                'End date should not be before start date'
              )
            }
            return schema.notRequired()
          }),
      }),

      linkUrl: string()
        .when('linkLabel', {
          is: (val: string) => val && val.length > 0,
          then: (schema) =>
            schema.required('Link URL is required if Link Label is present'),
          otherwise: (schema) => schema.notRequired(),
        })
        .max(200, 'Link URL can be at most ${max} characters')
        .test('is-https', 'Link URL must start with "https://"', (value) => {
          // only check for https if value exists
          if (value && value.length > 0) {
            return value.startsWith('https://')
          }
          return true // if the value is empty, skip the test
        }),
    },
    [['linkLabel', 'linkUrl']]
  )

  const formMethods = useForm({
    resolver: yupResolver(BannerDetailsSchema),
    mode: 'all',
    context: { isModalOpen },
    defaultValues: {
      Banner: {
        title: banner.title ?? '',
        content: banner.content ? banner.content : '',
        type: banner?.type || '',
        dismissable: banner?.dismissable || false,
        startDate: getBannerDates(banner).startDate,
        endDate: getBannerDates(banner).endDate,
      },
    },
  })

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = formMethods

  const onCancel = () => {
    toggleModalIsOpen()
    reset()
  }

  const handleSubmitWrapper = handleSubmit((data) =>
    onSubmitBanner({
      ...banner,
      id: banner.id,
      title: data.Banner.title,
      content: data.Banner.content,
      type: data.Banner.type as 'Urgent' | 'Informational',
      dismissable: data.Banner.dismissable,
      startDate: data.Banner.startDate
        ? formatISO(data.Banner.startDate)
        : undefined,
      endDate: data.Banner.endDate ? formatISO(data.Banner.endDate) : undefined,
    })
  )

  return (
    <Modal
      isOpen={isModalOpen}
      toggleIsOpen={() => {}}
      sx={{
        width: isMobile ? '100%' : '55%',
        height: heightModal,
      }}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmitWrapper}>
          <ModalFullHeader
            title="Edit Banner Details"
            subTitle={`Banner Type: ${banner.type}`}
            toggleIsOpen={toggleModalIsOpen}
          />
          <ModalFullBody heightModal={heightModal}>
            <Grid container>
              <Grid container>
                {Object.keys(errors).length > 0 && (
                  <Grid item xs={12} pb={4}>
                    <Alert severity="error">
                      Please correct errors to continue
                    </Alert>
                  </Grid>
                )}
              </Grid>
              <Grid container pt={4}>
                <Grid item xs={12} md={6} pt={{ md: 2 }}>
                  <InputLabel
                    htmlFor="Banner.title"
                    required
                    sx={{ fontSize: '16px', fontWeight: '100' }}
                  >
                    Banner Title
                  </InputLabel>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    multiline
                    required
                    error={isPresent(errors.Banner?.title)}
                    placeholder="Enter Banner title"
                    {...register('Banner.title')}
                    defaultValue={banner.title}
                    sx={{ width: '100%' }}
                    helperText="50 characters max"
                  />
                  {errors.Banner?.title?.message && (
                    <FormFieldErrorMessage
                      message={errors.Banner.title.message}
                    />
                  )}
                </Grid>
              </Grid>

              <Grid container pt={4}>
                <Grid item xs={12} md={6} pt={{ md: 2 }}>
                  <InputLabel
                    htmlFor="Banner.content"
                    required
                    sx={{ fontSize: '16px', fontWeight: '100' }}
                  >
                    Banner Content
                  </InputLabel>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={5}
                    required
                    error={isPresent(errors.Banner?.content)}
                    placeholder="Enter Banner content"
                    inputProps={{ maxLength: 150 }}
                    {...register('Banner.content')}
                    defaultValue={banner.content}
                    sx={{ width: '100%' }}
                    helperText="150 characters max"
                  />
                  {errors.Banner?.content?.message && (
                    <FormFieldErrorMessage
                      message={errors.Banner.content.message}
                    />
                  )}
                </Grid>
              </Grid>

              <Grid container pt={4}>
                <Grid item xs={12} md={6} pt={{ md: 2 }}>
                  <InputLabel
                    htmlFor="Banner.content"
                    sx={{ fontSize: '16px', fontWeight: '100' }}
                  >
                    Banner Type
                  </InputLabel>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="Banner.type"
                    control={control}
                    rules={{ required: 'Banner Type is required' }}
                    render={({ field }) => (
                      <RadioGroup {...field}>
                        {systemBannerTypes.map((type) => (
                          <FormControlLabel
                            key={type}
                            value={type}
                            control={<Radio />}
                            label={type}
                          />
                        ))}
                      </RadioGroup>
                    )}
                  />
                </Grid>
              </Grid>

              <Grid container pt={4}>
                <Grid item xs={12} md={6} pt={{ md: 2 }}>
                  <InputLabel
                    htmlFor="Banner.dismissable"
                    sx={{ fontSize: '16px', fontWeight: '100' }}
                  >
                    Allow Banner to be dismissed?
                  </InputLabel>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="Banner.dismissable"
                    control={control}
                    rules={{ required: 'Banner Type is required' }}
                    render={({ field }) => (
                      <RadioGroup {...field}>
                        <FormControlLabel
                          label="Yes"
                          value="true"
                          control={<Radio id="dismissable-yes" />}
                        />
                        <FormControlLabel
                          label="No"
                          value="false"
                          control={<Radio id="dismissable-no" />}
                        />
                      </RadioGroup>
                    )}
                  />
                </Grid>
              </Grid>

              <Grid container pt={4}>
                <Grid item xs={12} md={6} pt={{ md: 2 }}>
                  <InputLabel
                    htmlFor="Banner.deliveryDate"
                    sx={{ fontSize: '16px', fontWeight: '100' }}
                  >
                    Start Date
                  </InputLabel>
                </Grid>
                <Grid item xs={12} md={6}>
                  <AdornedDateTimeField
                    name="Banner.startDate"
                    control={control}
                  />
                  {errors.Banner?.startDate?.message && (
                    <FormFieldErrorMessage
                      message={errors.Banner?.startDate.message}
                    />
                  )}
                </Grid>
              </Grid>

              <Grid container pt={4}>
                <Grid item xs={12} md={6} pt={{ md: 2 }}>
                  <InputLabel
                    htmlFor="Banner.endDate"
                    sx={{ fontSize: '16px', fontWeight: '100' }}
                  >
                    End Date
                  </InputLabel>
                </Grid>
                <Grid item xs={12} md={6}>
                  <AdornedDateTimeField
                    name="Banner.endDate"
                    control={control}
                  />
                  {errors.Banner?.endDate?.message && (
                    <FormFieldErrorMessage
                      message={errors.Banner?.endDate.message}
                    />
                  )}
                </Grid>
              </Grid>
            </Grid>
          </ModalFullBody>
          <ModalFullFooter>
            <Button variant="text" onClick={() => onCancel()}>
              Cancel
            </Button>
            <ActivityButton
              disabled={!!errors.Banner}
              active={!!updateIsActive}
              type="submit"
              variant="contained"
            >
              Save
            </ActivityButton>
          </ModalFullFooter>
        </form>
      </FormProvider>
    </Modal>
  )
}
